/**
 * Application_Form — onboarding-routing pure logic
 * (member-onboarding-flow spec, task 1.1; originally application-form-builder
 * spec, task 10.1).
 *
 * This module holds the I/O-free heart of the post-sign-in journey: where a
 * freshly authenticated Member lands after sign-in. Following the
 * member-onboarding-flow reshape, the destination is a *total function of a
 * single derived bit* — whether the Member has completed the
 * Contact_Intake_Form — so the same decision runs on the onboarding router
 * (frontend) and on the `getOnboardingDestination` server op, mirroring the
 * other pure modules in this directory (`phaseGating.ts`,
 * `titlePreferences.ts`) and the wider precedent
 * (`division-registry/visibility.ts`, `person-service/personLogic.ts`). There
 * is no AWS SDK and no I/O here — only the routing rule.
 *
 * What lives here:
 *   - `Destination` — the discriminated union of every place onboarding can
 *     send a Member. The union is retained intact so `mapDestinationToPath`
 *     (and its Property 5 test) stay stable; the `APPLICATION` /
 *     `RESUME_RESPONSE` / `APPLICATION_STATUS` arms remain valid types but are
 *     no longer emitted by onboarding.
 *   - `RoutingState` — the single derived input the decision reads:
 *     `hasCompletedContactIntake` (a Person exists with `dateOfBirth` +
 *     `address`).
 *   - `resolveDestination(state)` — the two-branch total routing function
 *     (Req 1.2, 1.6).
 *
 * Validates: Requirements 1.2, 1.6, 2.3, 6.1, 6.3
 */

// ---------------------------------------------------------------------------
// Destinations

/**
 * Every place the onboarding router can send a Member after sign-in. The
 * destinations that open a specific application carry the `competitionId` they
 * resolved against so the caller never has to re-derive it.
 *
 *   - `CONTACT_INTAKE`     — the mandatory contact-details gate (Req 1.6).
 *   - `RESUME_RESPONSE`    — valid type; no longer emitted by onboarding.
 *   - `APPLICATION_STATUS` — valid type; no longer emitted by onboarding.
 *   - `APPLICATION`        — valid type; no longer emitted by onboarding.
 *   - `DASHBOARD`          — the exactly-one post-intake destination (Req 1.2).
 *
 * The union is deliberately kept intact (all five arms remain valid types) so
 * `mapDestinationToPath` and its Property 5 test stay unchanged; the three
 * application-bound arms are simply never produced here now.
 */
export type Destination =
  | { kind: 'CONTACT_INTAKE' }
  | { kind: 'APPLICATION'; competitionId: string }
  | { kind: 'RESUME_RESPONSE'; competitionId: string }
  | { kind: 'APPLICATION_STATUS'; competitionId: string }
  | { kind: 'DASHBOARD' };

// ---------------------------------------------------------------------------
// Routing state

/**
 * The derived state the (narrowed) routing decision reads. The single field is
 * computed by the caller from the Person record — this module never performs
 * I/O, it only interprets the derived state.
 */
export interface RoutingState {
  /**
   * Whether the Member has completed the Contact_Intake_Form — derived as a
   * Person record existing with both `dateOfBirth` and a current contact
   * `address` (Req 1.6). Until this is true, the Member is routed to the
   * contact-intake gate.
   */
  hasCompletedContactIntake: boolean;
}

// ---------------------------------------------------------------------------
// Routing decision

/**
 * The post-login destination, derived as a *total function* of whether the
 * Member has completed contact intake (Req 1.2, 1.6). The rule:
 *
 *   1. Contact intake not complete → `CONTACT_INTAKE` (Req 1.6). The Member is
 *      routed to the contact-intake gate on any login until intake is done.
 *   2. Otherwise → `DASHBOARD` (Req 1.2). This is the exactly-one post-intake
 *      destination, independent of any active competition, response, season, or
 *      participation (Req 2.3, 6.1, 6.3) — no full-screen application
 *      destination is ever produced by onboarding.
 *
 * Pure and total: every `RoutingState` maps to exactly one `Destination`, and
 * the input is never mutated.
 */
export function resolveDestination(state: RoutingState): Destination {
  if (!state.hasCompletedContactIntake) {
    return { kind: 'CONTACT_INTAKE' };
  }

  return { kind: 'DASHBOARD' };
}
